
import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, TrendingDown } from "lucide-react";
import { MetricTooltip } from "../../advanced/analytics/MetricTooltip";
import type { MortgageData } from "../../CalculatorTabs";
import { 
  calculateEffectiveSellerCredit,
  safeNumberConversion
} from "../../utils/transactionSafeCalculations";

interface CashDueAtClosingProps {
  mortgageData: MortgageData;
}

export const CashDueAtClosing: React.FC<CashDueAtClosingProps> = ({ mortgageData }) => {
  const calculationResults = useMemo(() => {
    const downPayment = safeNumberConversion(mortgageData.downPayment, 0);
    const originalClosingCosts = safeNumberConversion(mortgageData.closingCosts.total, 0);
    const sellerCreditAmount = safeNumberConversion(mortgageData.closingCosts.sellerCreditAmount, 0);
    const earnestMoney = safeNumberConversion(mortgageData.earnestMoneyDeposit, 0);
    
    const validSellerCreditAmount = Math.max(0, sellerCreditAmount);
    const effectiveSellerCredit = calculateEffectiveSellerCredit(validSellerCreditAmount, originalClosingCosts);
    const adjustedClosingCosts = Math.max(originalClosingCosts - effectiveSellerCredit, 0);
    const totalBeforeEarnest = downPayment + adjustedClosingCosts;
    const totalCashDue = Math.max(0, totalBeforeEarnest - earnestMoney);
    const savingsAmount = Math.max(0, effectiveSellerCredit);
    
    return {
      downPayment,
      originalClosingCosts,
      effectiveSellerCredit,
      adjustedClosingCosts,
      earnestMoney,
      totalCashDue,
      savingsAmount
    };
  }, [
    mortgageData.downPayment,
    mortgageData.closingCosts.total,
    mortgageData.closingCosts.sellerCreditAmount,
    mortgageData.sellerCredit,
    mortgageData.homePrice,
    mortgageData.earnestMoneyDeposit
  ]);

  const {
    downPayment,
    originalClosingCosts,
    effectiveSellerCredit,
    adjustedClosingCosts,
    earnestMoney,
    totalCashDue,
    savingsAmount
  } = calculationResults;

  // Only show seller credit if it's meaningful (greater than $0)
  const showSellerCredit = effectiveSellerCredit > 0;

  return (
    <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-green-800">
          <DollarSign className="w-5 h-5" />
          Cash Due at Closing <MetricTooltip metric="cashDueAtClosing" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700">Down Payment:</span>
            <span className="font-semibold text-gray-900">${downPayment.toLocaleString()}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700">Total Closing Costs:</span>
            <span className="font-semibold text-gray-900">${originalClosingCosts.toLocaleString()}</span>
          </div>
          
          {showSellerCredit && (
            <div className="flex justify-between items-center text-green-600">
              <span className="text-sm flex items-center gap-1">
                <TrendingDown className="w-4 h-4" />
                Seller Credit Applied:
              </span>
              <span className="font-semibold">${effectiveSellerCredit.toLocaleString()}</span>
            </div>
          )}
          
          {showSellerCredit && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Adjusted Closing Costs:</span>
              <span className="font-semibold text-gray-900">${adjustedClosingCosts.toLocaleString()}</span>
            </div>
          )}

          {earnestMoney > 0 && (
            <div className="flex justify-between items-center text-green-600">
              <span className="text-sm flex items-center gap-1">
                <TrendingDown className="w-4 h-4" />
                Earnest Money Deposit:
              </span>
              <span className="font-semibold">-${earnestMoney.toLocaleString()}</span>
            </div>
          )}
          
          <div className="h-px bg-gray-300 my-2" />
          
          <div className="flex justify-between items-center">
            <span className="font-bold text-green-800">Total Cash Due at Closing:</span>
            <span className="font-bold text-xl text-green-800">
              ${totalCashDue.toLocaleString()}
            </span>
          </div>
        </div>
        
        {showSellerCredit && (
          <div className="p-3 bg-green-100 border border-green-200 rounded-lg">
            <p className="text-xs text-green-700">
              <strong>✅ You'll save ${savingsAmount.toLocaleString()} in closing costs thanks to the seller credit.</strong>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
